import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { MDBDataTable } from 'mdbreact';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { activateAuthLayout } from '../../../../store/actions';
import SweetAlert from 'react-bootstrap-sweetalert';
import ImageDropzone from '../../../../components/ImageDropzone';
import { getLogoUrl } from '../../../../helpers/common';

const GET_PARTNERS = gql`
  query {
    Partners {
      PartnerId
      Name
      Logo
    }
  }
`;

const CREATE_PARTNER = gql`
  mutation createPartner($PartnerInput: PartnerInput) {
    createPartner(input: $PartnerInput) {
      PartnerId
      Name
      Logo
    }
  }
`;

const UPDATE_PARTNER = gql`
  mutation updatePartner($PartnerInput: PartnerInput) {
    updatePartner(input: $PartnerInput) {
      PartnerId
      Name
      Logo
    }
  }
`;

const DELETE_PARTNER = gql`
  mutation deletePartner($PartnerId: ID!) {
    deletePartner(PartnerId: $PartnerId) {
      PartnerId
      Name
    }
  }
`;

const UPLOAD_PARTNER_LOGO = gql`
  mutation uploadPartnerLogo($file: Upload!) {
    uploadPartnerLogo(file: $file)
  }
`;

const ManagePartners = ({ activateAuthLayout }) => {
  const { data, loading, refetch } = useQuery(GET_PARTNERS);
  const [tableData, setTableData] = useState({
    columns: [
      {
        label: ['Partner Name', <i key="partner_name" className="fa fa-sort ml-2"></i>],
        field: 'partner_name',
        sort: 'asc',
      },
      {
        label: 'Action',
        field: 'actions',
        sort: 'disabled',
      },
    ],
  });

  const [formModal, setFormModal] = useState(false);
  const [formInput, setFormInput] = useState(null);
  const [formType, setFormType] = useState('create');
  const [logoFile, setLogoFile] = useState(null);
  const [mutateResult, setMutateResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [createPartner] = useMutation(CREATE_PARTNER);
  const [updatePartner] = useMutation(UPDATE_PARTNER);
  const [deletePartner] = useMutation(DELETE_PARTNER);
  const [uploadPartnerLogo] = useMutation(UPLOAD_PARTNER_LOGO);

  const showFormModal = useCallback((partner = null) => {
    setFormModal(prev => !prev);
    setFormInput(partner);
    setFormType(partner ? 'edit' : 'create');
    setLogoFile(null);
  }, []);

  useEffect(() => {
    activateAuthLayout();
    refetch();
  }, [activateAuthLayout, refetch]);

  useEffect(() => {
    if (!loading && data) {
      setTableData(prev => ({
        ...prev,
        rows: data.Partners.map(partner => ({
          partner_name: partner.Name,
          actions: (
            <div>
              <Button color="info" size="sm" className="mr-2" onClick={() => showFormModal(partner)}>Edit</Button>
              <Button color="danger" size="sm" onClick={() => setDeleteTarget(partner)}>Delete</Button>
            </div>
          ),
        })),
      }));
    }
  }, [data, loading, showFormModal]);

  const onLogoDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    uploadPartnerLogo({ variables: { file } }).then(({ data }) => {
      const filename = data.uploadPartnerLogo;
      setLogoFile(Object.assign(file, { preview: getLogoUrl(filename) }));
      setFormInput(prev => ({ ...prev, Logo: filename }));
    });
  };

  const onLogoClear = () => {
    setLogoFile(null);
    setFormInput(prev => ({ ...prev, Logo: null }));
  };

  const updateForm = (field, value) => {
    setFormInput(prev => ({ ...prev, [field]: value }));
  };

  const save = (e) => {
    e.preventDefault();
    if (formType === 'edit') {
      updatePartner({
        variables: {
          PartnerInput: {
            PartnerId: formInput.PartnerId,
            Name: formInput.Name,
            Logo: formInput.Logo || null,
          },
        },
      }).then(() => {
        refetch();
        setMutateResult({ title: 'Success', message: 'Partner updated successfully', type: 'success', showConfirm: false });
        showFormModal();
      });
    } else {
      createPartner({
        variables: {
          PartnerInput: {
            Name: formInput.Name,
            Logo: formInput.Logo || null,
          },
        },
      }).then(() => {
        refetch();
        setMutateResult({ title: 'Success', message: 'Partner created successfully', type: 'success', showConfirm: false });
        showFormModal();
      });
    }
  };

  const deleteSelected = useCallback((partner) => {
    deletePartner({ variables: { PartnerId: partner.PartnerId } }).then(() => {
      refetch();
      setMutateResult({ title: 'Success', message: 'Partner deleted successfully', type: 'success', showConfirm: false });
    }).catch((err) => {
      setMutateResult({ title: 'Error', message: err.graphQLErrors?.[0]?.message || 'Failed to delete partner.', type: 'danger', showConfirm: true });
    });
  }, [deletePartner, refetch]);

  return (
    <React.Fragment>
      {deleteTarget && (
        <SweetAlert
          warning
          title="Confirm"
          onConfirm={() => { deleteSelected(deleteTarget); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
          showCancel
          confirmBtnText="Delete"
          cancelBtnText="Cancel"
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="secondary"
        >
          Delete partner "{deleteTarget.Name}"?
        </SweetAlert>
      )}
      <div className="content">
        <div className="container-fluid">
          <div className="page-title-box">
            <Row className="align-items-center">
              <Col sm="6">
                <h4 className="page-title">Partners</h4>
              </Col>
              <Col sm="6" className="text-right">
                <Button color="success" onClick={() => showFormModal()}>Create New Partner</Button>
              </Col>
            </Row>
          </div>
          <div className="rounded bg-white p-4">
            {!loading && tableData.rows && (
              <MDBDataTable
                sortable
                striped
                borderless
                theadColor="table-dark"
                hover
                responsive
                noBottomColumns
                noRecordsFoundLabel="No Partners Found"
                data={tableData}
              />
            )}
          </div>
        </div>
        <Modal isOpen={formModal} toggle={() => showFormModal()}>
          <ModalHeader toggle={() => showFormModal()}>
            {formType === 'edit' ? 'Edit Partner' : 'Create New Partner'}
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={save}>
              <FormGroup>
                <Label>Partner Name <small className="text-danger">*</small></Label>
                <Input
                  type="text"
                  placeholder="Partner Name"
                  defaultValue={formInput?.Name}
                  onChange={(e) => updateForm('Name', e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>Logo</Label>
                <ImageDropzone
                  onDrop={onLogoDrop}
                  onClear={onLogoClear}
                  imageUrl={logoFile?.preview ?? (formInput?.Logo ? getLogoUrl(formInput.Logo) : null)}
                  name="partner logo"
                />
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => showFormModal()}>Cancel</Button>
            <Button
              color="primary"
              onClick={save}
              disabled={!formInput?.Name}
            >
              Save
            </Button>
          </ModalFooter>
        </Modal>
        {mutateResult && (
          <SweetAlert
            type={mutateResult.type}
            title={mutateResult.title}
            onConfirm={() => setMutateResult(null)}
            showConfirm={mutateResult.showConfirm}
            timeout={mutateResult.showConfirm ? 0 : 2500}
          >
            {mutateResult.message}
          </SweetAlert>
        )}
      </div>
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(ManagePartners);
